import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import { useInvalidateNotifications } from '@/hooks/use-invalidate-notifications';
import { useAppStore } from '@/stores/app-store';
import type { QueryConfig } from '@/lib/react-query';
import type { WholesalerDto } from '@/types/auth';
import type { TcapSettingsFormValues } from '@/features/payments/types/form-schemas';

// ─── Fetchers ─────────────────────────────────────────────────────────────────

const fetchWholesalerCurrent = async (): Promise<WholesalerDto> => {
  const { data } = await api.get<WholesalerDto>(API_ROUTES.wholesalers.current);
  return data;
};

// PATCH /wholesalers/current returns 204 No Content (undocumented response schema) — refetch
// via invalidation below rather than trusting an unverified response body.
const updateWholesalerCurrent = async (payload: TcapSettingsFormValues): Promise<void> => {
  await api.patch(API_ROUTES.wholesalers.current, payload);
};

// ─── Query options factories ───────────────────────────────────────────────────

export const getWholesalerCurrentQueryOptions = (wholesalerId: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.wholesalers.current(wholesalerId),
    queryFn: fetchWholesalerCurrent,
    staleTime: 0,
    refetchOnMount: 'always' as const,
  });

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useWholesalerCurrent = (
  queryConfig?: QueryConfig<typeof getWholesalerCurrentQueryOptions>,
) => {
  const wholesalerId = useAppStore(
    (state) => state.selectedWholesalerId ?? state.currentUser?.wholesalers[0]?.id ?? null,
  );

  // `enabled` stays last: callers may pass polling options, but never re-enable the query
  // for a missing wholesaler id (that would send 'none' to the backend).
  return useQuery({
    ...getWholesalerCurrentQueryOptions(wholesalerId ?? 'none'),
    ...queryConfig,
    enabled: wholesalerId != null,
  });
};

export const useUpdateTcapSettings = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  const wholesalerId = useAppStore(
    (state) => state.selectedWholesalerId ?? state.currentUser?.wholesalers[0]?.id ?? null,
  );
  return useMutation({
    mutationFn: (payload: TcapSettingsFormValues) => updateWholesalerCurrent(payload),
    onSuccess: () => {
      if (wholesalerId != null) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wholesalers.current(wholesalerId),
        });
      }
      invalidateNotifications();
      toaster.create({
        type: 'success',
        title: DICTIONARY.dashboard.wholesalerSettings.toast.tcapSettingsSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.dashboard.wholesalerSettings.toast.tcapSettingsError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
